import React, { useState, useEffect, useCallback } from 'react';
import { Store, Edit2, Eye, Globe, Link2, X, Building2, MapPin, Phone, MessageCircle, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline browser-chrome website preview (matches hero right-side card aesthetic)
const ListingWebsitePreview = ({ item }) => {
  const s = item.settings || {};
  const highlights = (s.highlights || []).slice(0, 4);
  const origin = typeof window !== 'undefined' ? window.location.origin : 'rentkollect.com';
  const urlText = `${origin.replace(/^https?:\/\//, '')}/listings/${item.property_id}`;

  return (
    <div
      data-testid={`listing-preview-${item.property_id}`}
      className="relative bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm"
    >
      {/* Browser chrome */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-gray-100 bg-gray-50">
        <span className="w-2 h-2 rounded-full bg-red-400"></span>
        <span className="w-2 h-2 rounded-full bg-yellow-400"></span>
        <span className="w-2 h-2 rounded-full bg-green-400"></span>
        <div className="flex-1 text-center text-[10px] text-gray-400 font-mono truncate">
          {urlText}
        </div>
      </div>

      {/* Cover band */}
      <div className="relative h-28 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 overflow-hidden">
        {s.cover_image ? (
          <img
            src={s.cover_image}
            alt={item.property_name}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/40">
            <ImageIcon size={32} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent"></div>
        <div className="absolute bottom-2 left-3 right-3 text-white">
          <p className="text-[10px] uppercase tracking-widest opacity-80 flex items-center gap-1">
            <MapPin size={10} />
            <span className="truncate">{item.property_address || '—'}</span>
          </p>
          <h4 className="text-sm font-bold truncate">{s.headline || item.property_name}</h4>
        </div>
        {s.published ? (
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 bg-green-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
            <span className="w-1 h-1 bg-white rounded-full animate-pulse"></span>
            LIVE
          </span>
        ) : (
          <span className="absolute top-2 right-2 bg-gray-700 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
            DRAFT
          </span>
        )}
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100 text-center">
        <div className="py-2">
          <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">Vacant</p>
          <p className="text-sm font-bold text-emerald-600">{item.vacant_rooms}</p>
        </div>
        <div className="py-2">
          <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">Total</p>
          <p className="text-sm font-bold text-gray-900">{item.total_rooms}</p>
        </div>
        <div className="py-2">
          <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">From</p>
          <p className="text-sm font-bold text-blue-600">
            {s.starting_rent ? `₹${Number(s.starting_rent).toLocaleString('en-IN')}` : '—'}
          </p>
        </div>
      </div>

      {/* Description + highlights */}
      <div className="p-3 space-y-2">
        <p className="text-xs text-gray-600 line-clamp-2 leading-snug min-h-[2rem]">
          {s.description || <em className="text-gray-400">No description yet — click Edit to add one.</em>}
        </p>
        {highlights.length > 0 ? (
          <div className="flex gap-1 flex-wrap">
            {highlights.map((h) => (
              <span key={h} className="inline-block bg-blue-50 text-blue-700 text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                {h}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-[10px] text-gray-400 italic">No highlights added yet</p>
        )}
      </div>

      {/* Contact bar */}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-gray-100 bg-gray-50 text-[10px]">
        {s.contact_phone ? (
          <span className="inline-flex items-center gap-1 text-gray-600">
            <Phone size={10} /> {s.contact_phone}
          </span>
        ) : null}
        {s.contact_whatsapp ? (
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <MessageCircle size={10} /> WhatsApp
          </span>
        ) : null}
        {!s.contact_phone && !s.contact_whatsapp && (
          <span className="text-gray-400 italic">Add contact info to let tenants reach you</span>
        )}
      </div>
    </div>
  );
};

const Listings = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/listings/manage`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
    } catch { toast({ title: 'Error loading listings', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTogglePublish = async (item) => {
    try {
      await axios.put(`${API}/listings/manage/${item.property_id}`, {
        ...item.settings,
        published: !item.settings.published,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: item.settings.published ? 'Unpublished' : 'Published' });
      fetchData();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const copyPublicUrl = (propertyId) => {
    const origin = window.location.origin;
    const url = `${origin}/listings/${propertyId}`;
    navigator.clipboard.writeText(url);
    toast({ title: 'Public URL copied', description: url });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="listings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><Store /> Listings</h1>
          <p className="text-gray-600 mt-1">Publish your vacant rooms online — auto-generates a public listing page per property</p>
        </div>
        <a
          href="/listings"
          target="_blank"
          rel="noreferrer"
          data-testid="view-public-listings"
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
        >
          <Globe size={16} /> View Public Listings Page
        </a>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center">
          <Store size={64} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold mb-2">No properties to list</h3>
          <p className="text-gray-600">Create a property first to generate its public listing</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="listings-grid">
          {items.map(it => (
            <Card key={it.property_id} className="p-4 hover:shadow-lg transition-shadow flex flex-col gap-3" data-testid={`listing-card-${it.property_id}`}>
              {/* Property header row */}
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-base flex items-center gap-1.5">
                    <Building2 size={15} className="text-gray-400 flex-shrink-0" />
                    <span className="truncate">{it.property_name}</span>
                  </h3>
                </div>
              </div>

              {/* Website Preview */}
              <ListingWebsitePreview item={it} />

              {/* Action rail */}
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                <Button
                  data-testid={`toggle-publish-${it.property_id}`}
                  size="sm"
                  onClick={() => handleTogglePublish(it)}
                  className={it.settings.published ? 'bg-gray-600 hover:bg-gray-700' : 'bg-green-600 hover:bg-green-700'}
                >
                  {it.settings.published ? 'Unpublish' : 'Publish'}
                </Button>
                <Button data-testid={`edit-listing-${it.property_id}`} size="sm" variant="outline" onClick={() => setEditing(it)}>
                  <Edit2 size={14} className="mr-1" /> Edit
                </Button>
                {it.settings.published && (
                  <>
                    <Button data-testid={`copy-url-${it.property_id}`} size="sm" variant="outline" onClick={() => copyPublicUrl(it.property_id)} title="Copy public URL">
                      <Link2 size={14} />
                    </Button>
                    <a
                      href={`/listings/${it.property_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded border border-gray-300"
                      title="Open public page"
                      data-testid={`open-public-${it.property_id}`}
                    >
                      <Eye size={14} />
                    </a>
                  </>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <ListingFormModal
          editing={editing}
          onClose={() => setEditing(null)}
          onSuccess={fetchData}
          token={token}
        />
      )}
    </div>
  );
};

const ListingFormModal = ({ editing, onClose, onSuccess, token }) => {
  const [form, setForm] = useState({
    published: editing.settings.published ?? false,
    headline: editing.settings.headline || '',
    description: editing.settings.description || '',
    cover_image: editing.settings.cover_image || '',
    highlights: editing.settings.highlights || [],
    starting_rent: editing.settings.starting_rent || '',
    contact_phone: editing.settings.contact_phone || '',
    contact_whatsapp: editing.settings.contact_whatsapp || '',
  });
  const [highlightInput, setHighlightInput] = useState('');
  const [saving, setSaving] = useState(false);

  const addHighlight = () => {
    if (highlightInput.trim() && form.highlights.length < 8) {
      setForm({ ...form, highlights: [...form.highlights, highlightInput.trim()] });
      setHighlightInput('');
    }
  };
  const removeHighlight = (idx) => {
    setForm({ ...form, highlights: form.highlights.filter((_, i) => i !== idx) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        starting_rent: form.starting_rent ? parseFloat(form.starting_rent) : null,
      };
      await axios.put(`${API}/listings/manage/${editing.property_id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Listing saved' });
      onSuccess(); onClose();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Listing — {editing.property_name}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="listing-form">
          <div><Label>Headline</Label><Input data-testid="listing-headline" value={form.headline} onChange={e => setForm({...form, headline: e.target.value})} placeholder="e.g., Premium Co-Living in Gurugram" /></div>
          <div>
            <Label>Description</Label>
            <textarea data-testid="listing-description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Tell prospective tenants what makes this place special..." />
          </div>
          <div><Label>Cover Image URL</Label><Input data-testid="listing-cover" value={form.cover_image} onChange={e => setForm({...form, cover_image: e.target.value})} placeholder="https://..." /></div>

          <div>
            <Label>Highlights (max 8)</Label>
            <div className="flex gap-2 mb-2">
              <Input
                data-testid="listing-highlight-input"
                value={highlightInput}
                onChange={e => setHighlightInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addHighlight(); } }}
                placeholder="e.g., Free WiFi, AC, Near Metro"
              />
              <Button type="button" data-testid="listing-add-highlight" onClick={addHighlight} disabled={!highlightInput.trim() || form.highlights.length >= 8}>Add</Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {form.highlights.map((h, i) => (
                <span key={h} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  {h}
                  <button type="button" onClick={() => removeHighlight(i)} className="hover:text-red-600"><X size={10} /></button>
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div><Label>Starting Rent (₹)</Label><Input data-testid="listing-start-rent" type="number" value={form.starting_rent} onChange={e => setForm({...form, starting_rent: e.target.value})} /></div>
            <div><Label>Contact Phone</Label><Input data-testid="listing-phone" value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} /></div>
            <div><Label>WhatsApp</Label><Input data-testid="listing-whatsapp" value={form.contact_whatsapp} onChange={e => setForm({...form, contact_whatsapp: e.target.value})} /></div>
          </div>

          <label className="flex items-center gap-2 pt-2 cursor-pointer">
            <input data-testid="listing-published" type="checkbox" checked={form.published} onChange={e => setForm({...form, published: e.target.checked})} />
            <span className="font-medium">Publish this listing (visible on public page)</span>
          </label>

          <div className="flex gap-3 pt-2">
            <Button data-testid="listing-submit" type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={saving}>{saving ? 'Saving...' : 'Save Listing'}</Button>
            <Button data-testid="listing-cancel" type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Listings;
